/**
 * SOFSEK — Provisioning Gateway Logs (§19). Every synchronous pre-provisioning
 * check (external HR/ticketing tool asks "would granting this role breach a
 * rule?") is logged here with its disposition — APPROVED, PROVISIONALLY_
 * BLOCKED (parks into the standard exception workflow), or BLOCKED_BREACH.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.GATEKEEPER_LOG.LIST';

interface CodeOpt { code: string; name: string; }
interface LogRow {
  gatekeeper_log_id: number;
  app_user_account_id: number;
  role_id: number;
  gatekeeper_disposition_code: string;
  matched_rule_id: number | null;
  requested_at: string;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'account', header: 'Master User Token', defaultWidth: 200 },
  { key: 'role', header: 'Requested Role', defaultWidth: 200 },
  { key: 'disposition', header: 'Disposition', defaultWidth: 180 },
  { key: 'matched_rule', header: 'Matched Rule', defaultWidth: 200 },
  { key: 'requested_at', header: 'Requested', defaultWidth: 170 },
];

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function GatekeeperLogInner() {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<LogRow[]>([]);
  const [accounts, setAccounts] = useState<CodeOpt[]>([]);
  const [roles, setRoles] = useState<CodeOpt[]>([]);
  const [rules, setRules] = useState<CodeOpt[]>([]);
  const [dispositions, setDispositions] = useState<CodeOpt[]>([]);
  const [filter, setFilter] = useState('');
  const [dispositionFacet, setDispositionFacet] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [logRes, accRes, roleRes, ruleRes, dispRes] = await Promise.all([
        apiDb.from('sfk_gatekeeper_log').select('gatekeeper_log_id, app_user_account_id, role_id, gatekeeper_disposition_code, matched_rule_id, requested_at').order('requested_at', { ascending: false }),
        apiDb.from('sfk_app_user_account').select('app_user_account_id, external_username, csm_user_master!app_user_id ( display_name )'),
        apiDb.from('sfk_external_role').select('role_id, name'),
        apiDb.from('sfk_conflict_rule').select('rule_id, sfk_conflict_rule_tl ( locale_code, display_text )'),
        apiDb.from('sfk_gatekeeper_disposition').select('gatekeeper_disposition_code, sfk_gatekeeper_disposition_tl ( locale_code, display_text )'),
      ]);
      if (logRes.error) throw logRes.error;
      if (accRes.error) throw accRes.error;
      if (roleRes.error) throw roleRes.error;
      if (ruleRes.error) throw ruleRes.error;
      if (dispRes.error) throw dispRes.error;
      setRows((logRes.data ?? []) as unknown as LogRow[]);
      setAccounts(((accRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.app_user_account_id), name: `${(r.csm_user_master as { display_name?: string } | null)?.display_name ?? ''} (${r.external_username as string})` })));
      setRoles(((roleRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.role_id), name: r.name as string })));
      setRules(((ruleRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.rule_id), name: tlName(r.sfk_conflict_rule_tl, String(r.rule_id)) })));
      setDispositions(((dispRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.gatekeeper_disposition_code as string, name: tlName(r.sfk_gatekeeper_disposition_tl, r.gatekeeper_disposition_code as string) })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const resolveNameForAccount = (id: number) => accounts.find(a => a.code === String(id))?.name ?? String(id);
  const resolveNameForRole = (id: number) => roles.find(r => r.code === String(id))?.name ?? String(id);
  const resolveNameForRule = (id: number | null) => (id ? rules.find(r => r.code === String(id))?.name ?? String(id) : '—');
  const resolveNameForDisposition = (code: string) => dispositions.find(d => d.code === code)?.name ?? code;

  const detail = useExpandedRows<number>();
  // §17.B13 — related records for a gate decision: the master account, the role it
  // requested, and the rule that matched (if any), resolved (§17.B4).
  const buildRelatedGroups = (r: LogRow): RelatedChildGroup[] => [
    { key: 'account', title: t('sfk.gatekeeper_log.related.account', 'Master account'),
      items: [{ id: r.app_user_account_id, label: resolveNameForAccount(r.app_user_account_id) }] },
    { key: 'role', title: t('sfk.gatekeeper_log.related.role', 'Requested role'),
      items: [{ id: r.role_id, label: resolveNameForRole(r.role_id) }] },
    { key: 'rule', title: t('sfk.gatekeeper_log.related.rule', 'Matched rule'),
      items: r.matched_rule_id != null ? [{ id: r.matched_rule_id, label: resolveNameForRule(r.matched_rule_id) }] : [] },
  ];
  const hasRelated = (_r: LogRow) => true;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (dispositionFacet.length === 0 || dispositionFacet.includes(r.gatekeeper_disposition_code)) &&
      (!q || resolveNameForAccount(r.app_user_account_id).toLowerCase().includes(q) || resolveNameForRole(r.role_id).toLowerCase().includes(q))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, dispositionFacet, filter, accounts, roles]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.gatekeeper_log.title', 'Provisioning Gateway Logs')}
          description={t('sfk.gatekeeper_log.subtitle', 'Synchronous pre-provisioning gate decisions and their dispositions.')}
          actions={
            <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
          }
        />
        <StatStrip stats={[{ value: rows.length, label: t('common.total', 'Total') }, { value: filtered.length, label: t('common.shown', 'Shown') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.gatekeeper_log.facet.disposition', 'Disposition')} options={dispositions.map(d => ({ value: d.code, label: d.name }))} selected={dispositionFacet} onChange={setDispositionFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || dispositionFacet.length > 0} onClear={() => { setFilter(''); setDispositionFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.gatekeeper_log_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.gatekeeper_log_id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.gatekeeper_log_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'account' && resolveNameForAccount(r.app_user_account_id)}
                        {col.key === 'role' && resolveNameForRole(r.role_id)}
                        {col.key === 'disposition' && resolveNameForDisposition(r.gatekeeper_disposition_code)}
                        {col.key === 'matched_rule' && resolveNameForRule(r.matched_rule_id)}
                        {col.key === 'requested_at' && formatDateTime(r.requested_at)}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.gatekeeper_log_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 1} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}

export function SfkGatekeeperLogPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <GatekeeperLogInner />
    </PersonalizationProvider>
  );
}
